import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { setWorkspaceId } from './axios';

const PrivateRoute = React.memo(({ component: Component, auth, currentWorkspace, workspaces, requiresPasswordSetup, ...rest }) => {
  const location = useLocation();
  const workspaceId = currentWorkspace?._id;
  
  useEffect(() => {
    // If authenticated and has workspace, ensure URL includes workspace ID
    if (auth && workspaceId) {
      const currentPath = location.pathname;
      
      // Check if current path already starts with a workspace ID
      const pathParts = currentPath.split('/');
      const firstSegment = pathParts[1];
      
      // Check if the first segment is a valid workspace ID (24 character hex string)
      const isWorkspaceId = firstSegment && firstSegment.match(/^[a-fA-F0-9]{24}$/);
      
      // If URL has workspace ID, sync it with axios headers
      if (isWorkspaceId && firstSegment === workspaceId) {
        setWorkspaceId(workspaceId);
      }
    }
  }, [auth, workspaceId, location.pathname]);
  
  return (
    <Route {...rest} render={props => {
      // Check both Redux state and localStorage for authentication
      const isAuthenticated = auth || localStorage.getItem('token');

      if (!isAuthenticated) {
        return <Redirect to='/' />;
      }

      // SECURITY: Block access if password setup is required
      // Keep user on login screen until password is set
      // Check both Redux state and localStorage for extra safety on refresh
      const needsPasswordSetup = requiresPasswordSetup || localStorage.getItem('requiresPasswordSetup') === 'true';
      if (needsPasswordSetup) {
        return <Redirect to='/login' />;
      }
      
      // Extract workspace ID from URL if present
      const pathParts = props.location.pathname.split('/');
      const firstSegment = pathParts[1];
      
      // Check if the first segment is a valid workspace ID (24 character hex string)
      const urlWorkspaceId = firstSegment && firstSegment.match(/^[a-fA-F0-9]{24}$/) ? firstSegment : null;

      // Reason: redirect BEFORE the page mounts. Doing it in a useEffect ran after the page's own mount
      // effect had already fired its API calls without an x-workspace-id header (child effects run before
      // parent effects), so the first request resolved against a stale workspace ("Agent not found") and
      // only the remount on the prefixed URL succeeded.
      if (!urlWorkspaceId && workspaceId) {
        setWorkspaceId(workspaceId);
        return <Redirect to={`/${workspaceId}${props.location.pathname}${props.location.search}${props.location.hash}`} />;
      }

      // If URL has workspace ID but it doesn't match current workspace, check if user has access
      if (urlWorkspaceId && workspaceId && urlWorkspaceId !== workspaceId) {
        // Only show 403 if we're SURE they don't have access (workspaces list is loaded and workspace not found)
        const hasWorkspacesList = workspaces && Array.isArray(workspaces) && workspaces.length > 0;
        const hasAccessToWorkspace = hasWorkspacesList && workspaces.some(ws => ws._id === urlWorkspaceId);
        if (hasWorkspacesList && !hasAccessToWorkspace) {
          return (
            <Redirect to={{
              pathname: '/access-denied',
              state: {
                error: {
                  title: 'Workspace Access Denied',
                  message: 'You don\'t have access to this workspace.\nPlease check that you\'re using the correct workspace link.',
                  suggestions: [
                    'Verify you are using the correct workspace URL',
                    'Request access from the workspace owner',
                    'Contact support if you believe this is an error'
                  ]
                }
              }
            }} />
          )
        }
        // If workspaces list isn't loaded yet, or workspace IS in the list, allow access
        // The component will handle switching to the correct workspace
      }
      
      // Reason: every caller passes a hook-free inline element factory (props => <Admin>...</Admin>).
      // Invoking it (instead of mounting it as a component type) keeps the stable types (Admin, page)
      // as the reconciled tree, so a new function identity from an App re-render no longer
      // unmounts/remounts the whole layout and re-fires every mount-time API call.
      return Component(props);
    }} />
  );
}, (prevProps, nextProps) => {
  // Only re-render if auth, path, location, workspace, or password setup requirement changes.
  // Reason: Switch injects `location` on every navigation. When the workspace-prefix <Redirect> above fires for a
  // Route that matches both URL forms (e.g. DASHBOARD_PATHS), the same element is re-matched — if location is
  // ignored here the memo skips the re-render and the mounted <Redirect> (which renders null) stays on screen: blank page after login.
  return prevProps['auth'] === nextProps['auth'] &&
         prevProps['path'] === nextProps['path'] &&
         prevProps['location']?.['pathname'] === nextProps['location']?.['pathname'] &&
         prevProps['location']?.['search'] === nextProps['location']?.['search'] &&
         prevProps['currentWorkspace']?._id === nextProps['currentWorkspace']?._id &&
         prevProps['requiresPasswordSetup'] === nextProps['requiresPasswordSetup'];
});

PrivateRoute.propTypes = {
  auth: PropTypes.string,
  currentWorkspace: PropTypes.object,
  requiresPasswordSetup: PropTypes.bool
};

const mapStateToProps = ({ Auth, Settings }) => ({
  auth: Auth['auth'],
  currentWorkspace: Settings['currentWorkspace'],
  workspaces: Settings['workspaces'],
  requiresPasswordSetup: Auth['requiresPasswordSetup']
});

export default connect(mapStateToProps)(PrivateRoute);
